"use client";

import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Save as SaveIcon,
  X,
  Pencil,
  KeyRound,
  Search,
  RefreshCw,
  Send,
  Trash2,
  Sparkles,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import clsx from "clsx";
import { AdminShell } from "@/components/admin-shell";
import { FgCard } from "@/components/fg-card";
import { FgButton } from "@/components/fg-button";
import { FgInput } from "@/components/fg-input";
import { FgToast } from "@/components/fg-toast";
import { FgDialog } from "@/components/fg-dialog";
import { api } from "@/lib/api";

/* ────────────────────────────────────────────────────────────────────────── */

interface Tier {
  id: string;
  name: string;
  /** CEFR level scope — null means the tier is global (e.g. Bundle). */
  levelCode: string | null;
  /** Levels this tier's AI credits are valid on. Null = any level. */
  allowedLevels: string[] | null;
  /** AI credits granted per month on subscription. */
  aiCreditsPerMonth: number;
  priceCents: number;
  currency: string;
  monthsValid: number;
  monthsRevision: number;
  orderIndex: number;
  description: string | null;
  /** Dodo Payments product id (pdt_xxx). Required for paid tiers; Free can stay null. */
  dodoProductId: string | null;
}

interface User {
  id: string;
  email: string;
  fullName: string;
  isAdmin: boolean;
  isActive: boolean;
}

interface Level {
  id: string;
  name: string;
  displayName: string;
}

interface GrantRecord {
  id: string;
  userId: string;
  levelId: string;
  tierId: string;
  daysValid: number;
  createdAt: string;
}

type Tab = "tiers" | "grant";

/* ────────────────────────────────────────────────────────────────────────── */

export function SubscriptionsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("tiers");
  const [editing, setEditing] = useState<Tier | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<Tier | null>(null);

  const seedTiers = useMutation({
    mutationFn: async () =>
      (await api.post("/api/subscriptions/tiers/seed")).data as {
        ok: boolean;
        inserted: number;
        skipped: number;
        total: number;
      },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["tiers"] });
      FgToast.success(
        data.inserted > 0
          ? `Seeded ${data.inserted} new tier${data.inserted === 1 ? "" : "s"} (${data.skipped} already existed).`
          : `All ${data.skipped} tiers already exist — no changes.`,
      );
    },
    onError: (e) =>
      FgToast.error((e as { message?: string }).message ?? "Seed failed"),
  });

  const deleteTier = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/api/subscriptions/tiers/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tiers"] });
      FgToast.success("Tier deleted");
      setDeleting(null);
    },
    onError: (e) =>
      FgToast.error((e as { message?: string }).message ?? "Delete failed"),
  });

  return (
    <AdminShell>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[rgba(255,255,255,0.08)] pb-6 mb-8"
      >
        <div>
          <div className="flex items-center gap-2 mb-1">
            <KeyRound className="w-5 h-5 text-[#FFD60A]" />
            <span className="text-[11px] font-mono font-bold tracking-widest uppercase text-[#FFD60A]">
              SUBSCRIPTIONS
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#FAFAFA]">
            Subscriptions
          </h1>
          <p className="mt-1 text-sm text-[#8E8E93]">
            Tier catalog and manual access grants. iOS / Android receipts get routed
            by the mobile app via RevenueCat.
          </p>
        </div>
      </motion.div>

      <div className="mb-6 inline-flex items-center gap-1 p-1 rounded-xl bg-[#1C1C1E]/60 border border-[rgba(255,255,255,0.08)] backdrop-blur">
        {(
          [
            { id: "tiers" as const, label: "Tiers" },
            { id: "grant" as const, label: "Grant access" },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={clsx(
              "relative px-3.5 py-1.5 rounded-lg text-xs font-bold transition-colors",
              tab === t.id ? "text-[#0A0A0A]" : "text-[#8E8E93] hover:text-[#FAFAFA]",
            )}
          >
            {tab === t.id && (
              <motion.div
                layoutId="subscriptions-tab"
                className="absolute inset-0 bg-[#FFD60A] rounded-lg"
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            )}
            <span className="relative z-10">{t.label}</span>
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
        >
          {tab === "tiers" && (
            <TiersPanel
              onEdit={setEditing}
              onCreate={() => setCreating(true)}
              onDelete={setDeleting}
              onSeed={() => seedTiers.mutate()}
              isSeeding={seedTiers.isPending}
            />
          )}
          {tab === "grant" && <GrantPanel />}
        </motion.div>
      </AnimatePresence>

      {(creating || editing) && (
        <TierEditor
          tier={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["tiers"] });
            setCreating(false);
            setEditing(null);
          }}
        />
      )}

      {deleting && (
        <FgDialog
          open
          size="sm"
          tone="danger"
          title="Delete tier?"
          description={`"${deleting.name}" will be removed permanently. The backend refuses to delete a tier that still has active subscriptions — migrate them first.`}
          onClose={() => setDeleting(null)}
          footer={
            <>
              <FgButton variant="ghost" onClick={() => setDeleting(null)}>
                Cancel
              </FgButton>
              <FgButton
                variant="destructive"
                onClick={() => deleteTier.mutate(deleting.id)}
                loading={deleteTier.isPending}
                leftIcon={<Trash2 size={14} />}
              >
                Delete tier
              </FgButton>
            </>
          }
        />
      )}
    </AdminShell>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */

function TiersPanel({
  onEdit,
  onCreate,
  onDelete,
  onSeed,
  isSeeding,
}: {
  onEdit: (t: Tier) => void;
  onCreate: () => void;
  onDelete: (t: Tier) => void;
  onSeed: () => void;
  isSeeding: boolean;
}) {
  const tiers = useQuery<Tier[]>({
    queryKey: ["tiers"],
    queryFn: async () => (await api.get("/api/subscriptions/tiers")).data,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs text-[#8E8E93]">
          Public tier list — read by the landing page. Edit price, currency,
          validity, revision window.
        </p>
        <div className="flex items-center gap-2">
          <FgButton
            size="sm"
            variant="secondary"
            onClick={onSeed}
            loading={isSeeding}
            leftIcon={<Sparkles className="w-4 h-4" />}
          >
            Seed default tiers
          </FgButton>
          <FgButton
            size="sm"
            variant="primary"
            onClick={onCreate}
            leftIcon={<Plus className="w-4 h-4" />}
          >
            New tier
          </FgButton>
        </div>
      </div>
      {tiers.isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-24 rounded-xl bg-white/5 fg-skeleton" />
          ))}
        </div>
      )}
      {!tiers.isLoading && (tiers.data?.length ?? 0) === 0 && (
        <FgCard className="p-10 text-center">
          <Plus className="w-10 h-10 mx-auto text-[#8E8E93] mb-3" />
          <p className="text-sm text-[#FAFAFA] font-semibold">No tiers yet</p>
          <p className="mt-1 text-xs text-[#8E8E93]">
            Create the first one — the landing page reads from this list in
            real-time.
          </p>
        </FgCard>
      )}
      {tiers.data && tiers.data.length > 0 && (
        <div className="space-y-2">
          {tiers.data.map((t, idx) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: idx * 0.04 }}
            >
              <TierRow tier={t} onEdit={onEdit} onDelete={onDelete} />
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

function TierRow({
  tier,
  onEdit,
  onDelete,
}: {
  tier: Tier;
  onEdit: (t: Tier) => void;
  onDelete: (t: Tier) => void;
}) {
  return (
    <FgCard className="p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="rounded bg-[#FFD60A] px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-[#0A0A0A]">
              {tier.name}
            </span>
            <span className="text-base font-extrabold text-[#FAFAFA] counter-text">
              {(tier.priceCents / 100).toFixed(2)} {tier.currency}
            </span>
            <span className="text-[10px] font-mono text-[#8E8E93]">
              {tier.monthsValid}mo · {tier.monthsRevision}mo revision
            </span>
            {tier.aiCreditsPerMonth > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#FFD60A]/15 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-[#FFD60A] border border-[#FFD60A]/30">
                <Sparkles size={10} /> {tier.aiCreditsPerMonth} Learn Credits/mo
              </span>
            )}
            {tier.allowedLevels && tier.allowedLevels.length > 0 && (
              <span className="text-[10px] font-mono text-[#8E8E93]">
                scope: {tier.allowedLevels.join(" + ")}
              </span>
            )}
          </div>
          {tier.description && (
            <p className="text-xs text-[#8E8E93]">{tier.description}</p>
          )}
        </div>
        <div className="flex gap-2">
          <FgButton
            size="sm"
            variant="outline"
            onClick={() => onEdit(tier)}
            leftIcon={<Pencil size={12} />}
          >
            Edit
          </FgButton>
          <FgButton
            size="sm"
            variant="ghost"
            onClick={() => onDelete(tier)}
            leftIcon={<Trash2 size={14} />}
          >
            Delete
          </FgButton>
        </div>
      </div>
    </FgCard>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */

function GrantPanel() {
  const qc = useQueryClient();
  const [userId, setUserId] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [levelId, setLevelId] = useState("");
  const [tierId, setTierId] = useState("");
  const [daysValid, setDaysValid] = useState("60");
  const [granting, setGranting] = useState(false);

  const tiers = useQuery<Tier[]>({
    queryKey: ["tiers"],
    queryFn: async () => (await api.get("/api/subscriptions/tiers")).data,
  });
  const levels = useQuery<Level[]>({
    queryKey: ["levels"],
    queryFn: async () => (await api.get("/api/levels")).data,
  });

  // Lightweight user search — the backend has /api/admin/users?q=, so we
  // query on every search change. The route is gated behind AdminGuard.
  const users = useQuery<User[]>({
    queryKey: ["admin", "users", "search", userSearch],
    queryFn: async () => {
      if (!userSearch || userSearch.length < 2) return [];
      const params = new URLSearchParams({ q: userSearch, limit: "20" });
      return (await api.get(`/api/admin/users?${params.toString()}`)).data;
    },
    enabled: userSearch.length >= 2,
  });

  // Recent manual grants — the GET /api/subscriptions/grants endpoint is
  // small. If absent in the backend, surface a friendly empty state.
  const recent = useQuery<GrantRecord[]>({
    queryKey: ["admin", "grants"],
    queryFn: async () => {
      try {
        return (await api.get("/api/subscriptions/grants")).data;
      } catch {
        return [];
      }
    },
  });

  const grant = useMutation({
    mutationFn: async () =>
      (
        await api.post("/api/subscriptions/grant", {
          userId,
          levelId,
          tierId,
          daysValid: Number(daysValid) || 60,
        })
      ).data,
    onSuccess: () => {
      FgToast.success(`Granted ${daysValid}-day access`);
      qc.invalidateQueries({ queryKey: ["admin", "grants"] });
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      setUserId("");
      setUserSearch("");
      setGranting(false);
    },
    onError: (e) => FgToast.error((e as { message?: string }).message ?? "Grant failed"),
  });

  const userById = useMemo(() => {
    const map = new Map<string, User>();
    for (const u of users.data ?? []) map.set(u.id, u);
    return map;
  }, [users.data]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Form */}
      <FgCard className="p-5 lg:col-span-2">
        <h2 className="text-base font-bold text-[#FAFAFA] mb-1">Grant access</h2>
        <p className="text-xs text-[#8E8E93] mb-4">
          Bypass RevenueCat — useful for comp accounts, support, beta testers.
          The learner gets the standard access window from the chosen tier.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5">
              Learner
            </label>
            <div className="relative">
              <Search
                size={13}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8E8E93]"
              />
              <input
                value={userSearch}
                onChange={(e) => {
                  setUserSearch(e.target.value);
                  setUserId("");
                }}
                placeholder="Search by email or name…"
                className="w-full h-9 rounded-lg border border-[rgba(255,255,255,0.1)] bg-[#1C1C1E] pl-7 pr-3 text-sm text-[#FAFAFA] placeholder:text-[#8E8E93] focus:outline-none focus:border-[#FFD60A]"
              />
            </div>
            {users.isLoading && userSearch.length >= 2 && (
              <p className="mt-1 text-[10px] text-[#8E8E93]">Searching…</p>
            )}
            {users.data && users.data.length > 0 && (
              <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-[rgba(255,255,255,0.08)] divide-y divide-[rgba(255,255,255,0.04)] bg-[#141414]">
                {users.data.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => {
                      setUserId(u.id);
                      setUserSearch(`${u.email}`);
                    }}
                    className={clsx(
                      "w-full text-left px-3 py-2 text-xs transition-colors",
                      userId === u.id
                        ? "bg-[#FFD60A]/15 text-[#FFD60A]"
                        : "hover:bg-white/5 text-[#FAFAFA]",
                    )}
                  >
                    <div className="font-mono text-[11px]">{u.email}</div>
                    <div className="text-[10px] text-[#8E8E93]">
                      {u.fullName}
                      {u.isAdmin ? " · ADMIN" : ""}
                      {!u.isActive ? " · DISABLED" : ""}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {userId && (
              <p className="mt-2 text-[10px] font-mono text-[#FFD60A]">
                Selected · {userById.get(userId)?.email ?? userId}
              </p>
            )}
          </div>

          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5">
              Level
            </label>
            <select
              value={levelId}
              onChange={(e) => setLevelId(e.target.value)}
              className="h-9 w-full rounded-lg border border-[rgba(255,255,255,0.1)] bg-[#1C1C1E] px-3 text-sm text-[#FAFAFA] focus:outline-none focus:border-[#FFD60A]"
            >
              <option value="">— select —</option>
              {levels.data?.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} · {l.displayName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5">
              Tier
            </label>
            <select
              value={tierId}
              onChange={(e) => setTierId(e.target.value)}
              className="h-9 w-full rounded-lg border border-[rgba(255,255,255,0.1)] bg-[#1C1C1E] px-3 text-sm text-[#FAFAFA] focus:outline-none focus:border-[#FFD60A]"
            >
              <option value="">— select —</option>
              {tiers.data?.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} · {(t.priceCents / 100).toFixed(2)} {t.currency} · {t.monthsValid}mo
                </option>
              ))}
            </select>
          </div>

          <FgInput
            label="Days valid"
            type="number"
            value={daysValid}
            onChange={(e) => setDaysValid(e.target.value)}
            hint="Access window for the learner"
          />
        </div>

        <div className="mt-5 flex justify-end">
          <FgButton
            variant="primary"
            disabled={!userId || !levelId || !tierId}
            onClick={() => {
              setGranting(true);
              grant.mutate();
            }}
            loading={grant.isPending || granting}
            leftIcon={<Send className="w-4 h-4" />}
          >
            Grant access
          </FgButton>
        </div>
      </FgCard>

      {/* Recent grants list */}
      <FgCard className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-bold text-[#FAFAFA]">Recent grants</h2>
          <FgButton
            size="sm"
            variant="ghost"
            onClick={() => recent.refetch()}
            leftIcon={<RefreshCw className="w-3.5 h-3.5" />}
          >
            Refresh
          </FgButton>
        </div>
        {recent.data && recent.data.length > 0 ? (
          <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
            {recent.data.map((g) => (
              <div
                key={g.id}
                className="rounded-md bg-white/5 border border-[rgba(255,255,255,0.04)] px-2.5 py-1.5 text-[10px] font-mono"
              >
                <div className="text-[#FAFAFA] truncate">{g.userId}</div>
                <div className="text-[#8E8E93]">
                  level {g.levelId.slice(0, 8)} · {g.daysValid}d
                </div>
                <div className="text-[#8E8E93]">
                  {new Date(g.createdAt).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-[#8E8E93] text-center py-6">
            No recent grants.
          </p>
        )}
      </FgCard>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */

function TierEditor({
  tier,
  onClose,
  onSaved,
}: {
  tier: Tier | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(tier?.name ?? "");
  const [priceCents, setPriceCents] = useState(String(tier?.priceCents ?? 999));
  const [currency, setCurrency] = useState(tier?.currency ?? "USD");
  const [monthsValid, setMonthsValid] = useState(String(tier?.monthsValid ?? 2));
  const [monthsRevision, setMonthsRevision] = useState(String(tier?.monthsRevision ?? 2));
  const [aiCreditsPerMonth, setAiCreditsPerMonth] = useState(
    String(tier?.aiCreditsPerMonth ?? 0),
  );
  const [levelCode, setLevelCode] = useState(tier?.levelCode ?? "");
  const [allowedLevels, setAllowedLevels] = useState<string[]>(
    tier?.allowedLevels ?? [],
  );
  const [orderIndex, setOrderIndex] = useState(String(tier?.orderIndex ?? 100));
  const [description, setDescription] = useState(tier?.description ?? "");
  const [dodoProductId, setDodoProductId] = useState(tier?.dodoProductId ?? "");

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name: name.trim(),
        priceCents: Number(priceCents) || 0,
        currency,
        monthsValid: Number(monthsValid) || 1,
        monthsRevision: Number(monthsRevision) || 0,
        aiCreditsPerMonth: Number(aiCreditsPerMonth) || 0,
        levelCode: levelCode.trim() || null,
        allowedLevels: allowedLevels.length > 0 ? allowedLevels : null,
        orderIndex: Number(orderIndex) || 100,
        description: description || null,
        dodoProductId: dodoProductId.trim() || null,
      };
      return tier
        ? (await api.patch(`/api/subscriptions/tiers/${tier.id}`, body)).data
        : (await api.post("/api/subscriptions/tiers", body)).data;
    },
    onSuccess: () => {
      FgToast.success(tier ? "Tier updated" : "Tier created");
      onSaved();
    },
    onError: (e) => FgToast.error((e as { message?: string }).message ?? "Save failed"),
  });

  return (
    <FgCard className="mt-6 p-5">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">
          {tier ? `Editing ${tier.name}` : "New tier"}
        </h2>
        <button onClick={onClose} className="rounded-md p-2 text-[#8E8E93] hover:bg-white/10 hover:text-[#FAFAFA]">
          <X size={16} />
        </button>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FgInput label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
        <FgInput
          label="Price (cents)"
          type="number"
          value={priceCents}
          onChange={(e) => setPriceCents(e.target.value)}
        />
        <FgInput
          label="Currency"
          value={currency}
          onChange={(e) => setCurrency(e.target.value.toUpperCase())}
          hint="ISO 4217 — USD, EUR, ZAR, …"
        />
        <FgInput
          label="Months valid"
          type="number"
          value={monthsValid}
          onChange={(e) => setMonthsValid(e.target.value)}
        />
        <FgInput
          label="Months revision"
          type="number"
          value={monthsRevision}
          onChange={(e) => setMonthsRevision(e.target.value)}
          hint="Read-only window after expiry"
        />
        <FgInput
          label="Learn Credits per month"
          type="number"
          value={aiCreditsPerMonth}
          onChange={(e) => setAiCreditsPerMonth(e.target.value)}
          hint="Granted when this tier is subscribed. 0 = no Learn Credits."
        />
        <FgInput
          label="Order index"
          type="number"
          value={orderIndex}
          onChange={(e) => setOrderIndex(e.target.value)}
          hint="Display order — lower = first."
        />
        <FgInput
          label="Level code (single-tier scope)"
          value={levelCode}
          onChange={(e) => setLevelCode(e.target.value.toUpperCase())}
          placeholder="A1 / A2 / B1 / B2 / C1 — leave empty for multi-level"
          hint="If set, credits are scoped to this level. Empty = wildcard (any level)."
        />
        <LevelMultiSelect
          values={allowedLevels}
          onChange={setAllowedLevels}
        />
        <FgInput
          className="sm:col-span-2"
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <FgInput
          className="sm:col-span-2"
          label="Dodo product id"
          value={dodoProductId}
          onChange={(e) => setDodoProductId(e.target.value)}
          hint="Paste the pdt_xxx id from the Dodo Payments dashboard. Required for paid tiers — Free can stay blank."
        />
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <FgButton variant="ghost" onClick={onClose}>
          Cancel
        </FgButton>
        <FgButton
          variant="primary"
          onClick={() => save.mutate()}
          loading={save.isPending}
          leftIcon={<SaveIcon />}
        >
          Save
        </FgButton>
      </div>
    </FgCard>
  );
}

/**
 * Multi-select chip control for the `allowedLevels` field on a tier.
 * Stays in sync with the CEFR levels used across the admin portal.
 */
function LevelMultiSelect({
  values,
  onChange,
}: {
  values: string[];
  onChange: (next: string[]) => void;
}) {
  const ALL_LEVELS = ["A1", "A2", "B1", "B2", "C1"] as const;
  function toggle(lvl: string) {
    if (values.includes(lvl)) {
      onChange(values.filter((v) => v !== lvl));
    } else {
      onChange([...values, lvl].sort());
    }
  }
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5">
        Allowed levels (Learn Credits scope)
      </label>
      <div className="flex flex-wrap gap-1.5">
        {ALL_LEVELS.map((lvl) => {
          const on = values.includes(lvl);
          return (
            <button
              key={lvl}
              type="button"
              onClick={() => toggle(lvl)}
              className={
                "rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider transition-colors " +
                (on
                  ? "bg-[#FFD60A] text-[#0A0A0A]"
                  : "bg-[#1C1C1E] text-[#8E8E93] hover:text-[#FAFAFA]")
              }
            >
              {lvl}
            </button>
          );
        })}
      </div>
      <p className="mt-1 text-[10px] font-mono text-[#8E8E93]">
        Empty = any level. Tier auto-inherits the levelCode if it matches.
      </p>
    </div>
  );
}
